import React, { useContext, useMemo, ReactElement, useEffect } from 'react';
import { AddressSelectionContext } from './AddressSelectionProvider';
import { OrderMethods } from './components/order-methods';
import { RestaurantList } from './components/restaurant-list/RestaurantList';
import styled from 'styled-components';
import {
  PreselectedRestaurantOutOfReach,
  useRestaurantById,
  useRestaurants,
} from './useRestaurants';
import { typography } from '../../theme';
import { SteppedContainer, SteppedGroup } from '../container/SteppedContainer';
import { OrderTypeSpecificAddressSelection } from './OrderTypeSpecificAddressSelection';
import { InfoBox, InfoBoxTypes } from '../infobox';
import { LoadingIcon } from '../loading/LoadingIcon';
import { AnimatePresence, motion } from 'framer-motion';
import { storage } from '../../utils/storage';
import { analyticsEvents } from '../../utils/analytics/analytics';
import { OrderType } from '../../types';
import { trackInsightsException } from '../../utils/telemetry/appinsights';
import { useSearchParams } from 'next/navigation';

type Props = {
  preselectedRestaurantId?: number;
};

export const AddressSelection: React.FC<Props> = ({ preselectedRestaurantId }) => {
  const { setRestaurantId, setSearchQuery, setOrderType } = useContext(AddressSelectionContext);
  const searchParams = useSearchParams();

  useEffect(() => {
    /*
      We need to send page view event with custom path and href attributes
      for address selection modal to keep track of user flows better
      in Google Analytics.
    */
    if (typeof window === 'undefined') return;

    const path = window.location.pathname;
    const isHomepage = path === '/';
    const customPath = `${path}${isHomepage ? '' : '/'}toimitustapa`;
    const customHref = `${window.location.href.split('#')[0]}${isHomepage ? '' : '/'}toimitustapa`;
    analyticsEvents.pageView(customPath, customHref);

    return () => {
      analyticsEvents.pageView();
    };
  }, []);

  useEffect(() => {
    /*
      Check order type from possible url search params and
      update accordingly when modal is opened.
    */
    if (typeof window === 'undefined' || !searchParams) return;

    if (searchParams.get('kuljetus') !== null) {
      setOrderType(OrderType.DELIVERY);
    } else if (searchParams.get('nouto') !== null) {
      setOrderType(OrderType.PICKUP);
    } else if (searchParams.get('paikan-paalla') !== null) {
      setOrderType(OrderType.EATIN);
    }
  }, [setOrderType, searchParams]);

  const {
    restaurants,
    isRestaurantsLoading,
    isAllRestaurantsLoading,
    restaurantError,
    allRestaurantError,
  } = useRestaurants(preselectedRestaurantId);
  const { restaurant: preselectedRestaurant } = useRestaurantById(preselectedRestaurantId);

  useEffect(() => {
    if (preselectedRestaurantId && preselectedRestaurant) {
      setRestaurantId(preselectedRestaurantId);

      // Set search query to preselected restaurants name so pickup and eatin show nearby restaurants
      const searchQuery = preselectedRestaurant.displayName;
      setSearchQuery(searchQuery);
      storage.setItem('pickup_search_query', searchQuery);
    }
  }, [preselectedRestaurant, preselectedRestaurantId, setRestaurantId, setSearchQuery]);

  const preselectionOutOfReach = useMemo(
    () =>
      preselectedRestaurantId &&
      !restaurants.find((restaurant) => restaurant.restaurantId === preselectedRestaurantId),
    [preselectedRestaurantId, restaurants]
  );

  let error: ReactElement | null = null;

  if (allRestaurantError) {
    trackInsightsException('FRONTEND_ADDRESS_SELECTION_RESTAURANTS_ERROR', allRestaurantError);
    error = (
      <SteppedContainer primary stepNumber={1} title="Toimitustapa">
        <InfoBox type={InfoBoxTypes.ERROR}>
          <typography.ParagraphSmall>
            Kääk, pizzajärjestelmä näyttää kärähtäneen. Kokeile hetken päästä uudelleen.
          </typography.ParagraphSmall>
        </InfoBox>
      </SteppedContainer>
    );
  } else if (isAllRestaurantsLoading) {
    return (
      <Container>
        <LoadingIcon />
      </Container>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        key="address-book"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        transition={{ duration: 0.4 }}
        exit={{ height: 0, opacity: 0 }}
      >
        <AddressSelectionContainer>
          <SteppedGroup hideSteps={true}>
            {error ? (
              error
            ) : (
              <>
                <SteppedContainer primary stepNumber={1} title="Toimitustapa">
                  <OrderMethods />
                </SteppedContainer>
                <OrderTypeSpecificAddressSelection />
                <RestaurantList
                  restaurants={restaurants}
                  isLoading={isRestaurantsLoading}
                  error={
                    restaurantError
                      ? restaurantError
                      : preselectionOutOfReach
                        ? new PreselectedRestaurantOutOfReach()
                        : null
                  }
                />
              </>
            )}
          </SteppedGroup>
        </AddressSelectionContainer>
      </motion.div>
    </AnimatePresence>
  );
};

const Container = styled.div`
  display: grid;
  justify-content: center;
  align-content: center;
  width: 100%;
  min-height: 100px;
`;

const AddressSelectionContainer = styled.div`
  font: ${typography.textStyle};
  transition: color 0.1s ease-in;
`;
